import type { ReactNode } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { Icone, type NomIcone } from '../../../composants/icones.tsx';
import { useSession } from '../../../composants/session.tsx';
import { Montant } from '../../../composants/ui/index.ts';
import type { Tableau } from '../types.ts';

/**
 * « À surveiller aujourd’hui » — un REGISTRE d’exceptions, pas un tableau
 * de bord d’indicateurs.
 *
 * Sur un registre, on ne note pas ce qui va bien : un rayon plein ou un
 * client à jour n’y figurent jamais. Seules les EXCEPTIONS montent ici — au
 * plus deux ruptures de stock, les créances échues, les écarts signalés —
 * chacune avec le geste qui la traite. Le détail complet reste dans les
 * panneaux du dessous ; ici, seul ce qui réclame une décision aujourd’hui.
 *
 * Chaque ligne n’existe que si le serveur a envoyé la donnée (RG-143) ET que
 * l’utilisateur peut ouvrir l’écran de destination.
 */
export function BlocSurveiller({
  stocks,
  creances,
  anomalies,
}: {
  stocks: NonNullable<Tableau['stocks_faibles']>;
  creances: Tableau['creances'];
  anomalies: number;
}): ReactNode {
  const { peut } = useSession();
  const naviguer = useNavigate();

  const stocksTries = [...stocks].sort((a, b) => a.quantite - b.quantite);
  const points: {
    cle: string;
    marque: 'rust' | 'brick';
    icone: NomIcone;
    titre: string;
    detail: ReactNode;
    action?: { libelle: string; vers: string } | undefined;
  }[] = [];

  for (const ligne of stocksTries.slice(0, 2)) {
    points.push({
      cle: `${ligne.produit_id}-${ligne.boutique_id}`,
      marque: ligne.quantite <= 0 ? 'brick' : 'rust',
      icone: 'alerte',
      titre: `${ligne.designation} — ${ligne.quantite <= 0 ? 'rupture' : 'stock bas'}`,
      detail: (
        <>
          {ligne.quantite} restant{ligne.quantite > 1 ? 's' : ''}
          {ligne.boutique_code !== '' && ` à ${ligne.boutique_code}`} · seuil {ligne.seuil}
        </>
      ),
      action: peut('fournisseur.lire') ? { libelle: 'Commander', vers: '/fournisseurs' } : undefined,
    });
  }

  if (creances !== undefined && (creances.echu ?? 0) > 0) {
    points.push({
      cle: 'creances',
      marque: 'brick',
      icone: 'credit',
      titre: `${String(creances.nombre ?? 0)} créance(s) échue(s)`,
      detail: (
        <>
          <Montant valeur={creances.echu ?? 0} /> sur <Montant valeur={creances.encours ?? 0} /> d’encours
        </>
      ),
      action: peut('client.lire') ? { libelle: 'Relancer', vers: '/clients' } : undefined,
    });
  }

  if (anomalies > 0) {
    points.push({
      cle: 'anomalies',
      marque: 'rust',
      icone: 'anomalie',
      titre: `${String(anomalies)} écart(s) signalé(s)`,
      detail: 'Remises, caisses et numérotation à contrôler',
      action: peut('audit.lire') ? { libelle: 'Examiner', vers: '/anomalies' } : undefined,
    });
  }

  return (
    <div className="tdb-surveiller">
      <header className="tdb-surveiller__tete">
        <h2>À surveiller aujourd’hui</h2>
        <span>{points.length === 0 ? 'Rien à signaler' : `${String(points.length)} point(s)`}</span>
      </header>

      {points.length === 0 ? (
        <p className="table-vide">
          Aucune rupture, aucune créance échue, aucun écart signalé. Tout est en ordre.
        </p>
      ) : (
        points.map((point) => (
          <div key={point.cle} className={`tdb-ligne-alerte tdb-ligne-alerte--${point.marque}`}>
            <span className="tdb-ligne-alerte__icone">
              <Icone nom={point.icone} taille={16} />
            </span>
            <div className="tdb-ligne-alerte__corps">
              <div className="tdb-ligne-alerte__titre">{point.titre}</div>
              <div className="tdb-ligne-alerte__detail">{point.detail}</div>
            </div>
            {point.action !== undefined && (
              <BoutonAction action={point.action} surNaviguer={naviguer} />
            )}
          </div>
        ))
      )}
    </div>
  );
}

/** Isolé pour que TypeScript garde `action` non optionnel dans le clic. */
function BoutonAction({
  action,
  surNaviguer,
}: {
  action: { libelle: string; vers: string };
  surNaviguer: NavigateFunction;
}): ReactNode {
  return (
    <button
      type="button"
      className="tdb-ligne-alerte__action"
      onClick={() => void surNaviguer(action.vers)}
    >
      {action.libelle}
    </button>
  );
}
